import * as React from 'react';

import Uri from 'jsuri';
import _ from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type Rule<V = any> = (value: V, data?: { [key: string]: V }) => React.ReactNode;

type Validator<V, D extends { [key: string]: V }> = (rules: {
  [key: string]: Rule<V>[];
}) => (data: D) => { [key: string]: React.ReactNode };

type RuleGenerator1<T, R = any> = (arg: T) => Rule<R>;
type RuleGenerator2<T1, T2, R = any> = (arg1: T1, arg2: T2) => Rule<R>;
type isValidMaxLengthRuleGenerator = (originalLength: number, newMax: number, oldMax: number) => Rule;

/* eslint-disable consistent-return */
const isEmpty = (value: any): boolean => value === undefined || value === null || value === '';

function join<V>(rules: Rule<V>[]): Rule<V> {
  return (value: V, data?: { [key: string]: V }) =>
    rules.map((rule) => rule(value, data)).filter((error) => !!error)[0];
}

const isEmail: Rule = (value) => {
  if (!isEmpty(value) && !/^[A-Z0-9._%'+-]+@[A-Z0-9.-]+\.[A-Z]{2,100}$/i.test(value)) {
    return _t('Invalid email address');
  }
};

const isUrl: Rule = (value) => {
  if (!isEmpty(value) && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i.test(value)) {
    return _t('Invalid url');
  }
};

// Validate if a url starts with top level coursera domain
const isCourseraUrl: Rule = (value) => {
  if (!isEmpty(value) && !/^https?:\/\/(www.)?coursera.org\/[a-z0-9-\/]+$/i.test(value)) {
    return _t('Invalid url, valid example: https://www.coursera.org/your-slug-here');
  }
};

const isSlug: Rule = (value) => {
  if (!isEmpty(value) && !/^[a-z0-9-]+$/i.test(value)) {
    return _t('Invalid slug');
  }
};

const isHexColor: Rule = (value) => {
  if (!isEmpty(value) && !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
    return _t('Invalid hex color code');
  }
};

const isNotWhiteColor: Rule = (value) => {
  if (/^#([f]{3}|[f]{6})$/i.test(value)) {
    return _t('Hex color cannot be white (#FFFFFF)');
  }
};

// Input: http://www.abc.com/slug-name[/more-slug-names]
// If input is not url or only have top level, will return error
const isValidCourseraUrlWithValidSlug: Rule = (value) => {
  if (isCourseraUrl(value)) {
    return isCourseraUrl(value);
  }
  const uri = new Uri(value);
  if (uri.path() === '' || uri.path() === '/') {
    return _t('No url path provided');
  }
  const slugs = uri.path().substr(1).split('/');
  // check if each slug is valid
  slugs.forEach((item) => {
    if (isSlug(item)) {
      return isSlug(item);
    }
  });
};

const isRequired: Rule = (value) => {
  if (isEmpty(value)) {
    return _t('Required');
  }
};

const minLength: RuleGenerator1<number> = (min) => {
  return (value) => {
    if (!isEmpty(value) && value.length < min) {
      return <FormattedMessage message={_t('Must be at least {min} characters')} min={min} />;
    }
  };
};

const maxLength: RuleGenerator1<number> = (max) => {
  return (value) => {
    if (!isEmpty(value) && value.length > max) {
      return <FormattedMessage message={_t('Must be no more than {max} characters')} max={max} />;
    }
  };
};

const isValidMaxLength: isValidMaxLengthRuleGenerator = (originalLength, newMax, oldMax) => {
  return (value) => {
    if (originalLength <= newMax && value.length > newMax) {
      return <FormattedMessage message={_t('Must be no more than {max} characters')} max={newMax} />;
    } else if (value.length > oldMax) {
      return <FormattedMessage message={_t('Must be no more than {max} characters')} max={oldMax} />;
    }
    return null;
  };
};

const isInteger: Rule = (value) => {
  if (!Number.isInteger(Number(value))) {
    return _t('Must be an integer');
  }
};

const isInRange: RuleGenerator2<number, number> = (min, max) => {
  return (value) => {
    if (!isEmpty(value) && (Number(value) < Number(min) || Number(value) > Number(max))) {
      return <FormattedMessage message={_t('Must be a number between {min} and {max}')} min={min} max={max} />;
    }
  };
};

function isOneOf<V>(enumeration: V[]): Rule<V> {
  return (value: V) => {
    if (!_.includes(enumeration, value)) {
      const options = enumeration.join(', ');
      return <FormattedMessage message={_t('Must be one of:  {options}')} options={options} />;
    }
  };
}

const isMatch: RuleGenerator1<string> = (field) => {
  return (value, data) => {
    if (data && value !== data[field]) {
      return _t('Do not match');
    }
  };
};

function createValidator<V = any>(rules: { [key: string]: Rule<V> | Rule<V>[] }) {
  return (data: { [key: string]: V } = {}) => {
    const errors: { [key: string]: React.ReactNode } = {};
    Object.keys(rules).forEach((key) => {
      const rule = join(([] as Rule<V>[]).concat(rules[key])); // concat enables both functions and arrays of functions
      const error = rule(data[key], data);
      if (error) {
        errors[key] = error;
      }
    });
    return errors;
  };
}

export type { Rule, Validator, RuleGenerator1, RuleGenerator2 };

export default {
  isEmail,
  isUrl,
  isCourseraUrl,
  isSlug,
  isHexColor,
  isNotWhiteColor,
  isValidCourseraUrlWithValidSlug,
  isRequired,
  minLength,
  maxLength,
  isValidMaxLength,
  isInteger,
  isOneOf,
  isMatch,
  isInRange,
  createValidator,
};

export {
  isEmail,
  isUrl,
  isCourseraUrl,
  isSlug,
  isHexColor,
  isNotWhiteColor,
  isValidCourseraUrlWithValidSlug,
  isRequired,
  minLength,
  maxLength,
  isValidMaxLength,
  isInteger,
  isOneOf,
  isMatch,
  isInRange,
  createValidator,
};
